import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { useKnowledgeStats } from '@/hooks/useKnowledge'
import type { AgentSummary, KnowledgeStats } from '@/lib/api'
import { getAgentIcon } from '@/components/agents/AgentIcons'

const KB_CATEGORY_LABELS: Record<string, string> = {
  product: 'Product',
  regulation: 'Regulation',
  process: 'Process',
  faq: 'FAQ',
}

function timeAgo(iso: string | null): string {
  if (!iso) return 'NEVER'
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'JUST NOW'
  if (mins < 60) return `${mins}M AGO`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}H AGO`
  return `${Math.floor(hours / 24)}D AGO`
}

function KbStat({ label, value, tone }: { label: string; value: string; tone?: 'warn' }) {
  return (
    <div className="bg-cmd-high px-3 py-2">
      <p className="font-label text-[0.55rem] tracking-widest text-cmd-outline uppercase">
        {label}
      </p>
      <p
        className={`font-mono text-lg font-medium tabular-nums mt-0.5 ${
          tone === 'warn' ? 'text-[#ffd080]' : 'text-white'
        }`}
      >
        {value}
      </p>
    </div>
  )
}

/**
 * Hestia · Knowledge Base panel (TMA-93). Surfaces the internal-wiki article
 * counts, category breakdown, and recent edits. Rendered at the foot of the
 * Agents page — it is not a standalone route.
 */
export function KnowledgeBasePanel({ hestia }: { hestia: AgentSummary | undefined }) {
  const { data, isLoading, error } = useKnowledgeStats()
  const stats: KnowledgeStats | undefined = data?.data
  const Icon = getAgentIcon('knowledge-base')

  const statusLabel = hestia?.status ? hestia.status.toUpperCase() : 'NO TELEMETRY'
  const statusVariant: 'success' | 'outline' =
    hestia?.status === 'active' ? 'success' : 'outline'

  return (
    <div className="bg-cmd-container">
      <div className="relative">
        <div
          className="absolute left-0 top-0 bottom-0 w-1"
          style={{ backgroundColor: '#f4a261' }}
        />
        <div className="px-5 py-4 pl-6">
          {/* Header */}
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center bg-cmd-high text-base">
                {Icon ? <Icon size={32} /> : <span>📚</span>}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h2 className="font-sans text-base font-semibold text-white tracking-tight">
                    Hestia · Knowledge Base
                  </h2>
                  <Badge variant={statusVariant} className="text-[9px] px-1 py-0 font-mono">
                    {statusLabel}
                  </Badge>
                </div>
                <p className="font-label text-[0.6rem] text-cmd-outline uppercase mt-0.5">
                  Internal wiki · product · regulation · process · FAQ
                </p>
              </div>
            </div>
            {hestia && (
              <div className="text-right shrink-0">
                <p className="font-label text-[0.55rem] tracking-widest text-cmd-outline uppercase">
                  Last run
                </p>
                <p className="font-mono text-xs text-white tabular-nums mt-0.5">
                  {timeAgo(hestia.last_run_at)}
                </p>
              </div>
            )}
          </div>

          {error && (
            <p className="mt-3 font-label text-xs text-cmd-error">
              Could not load knowledge stats: {error.message}
            </p>
          )}

          {/* Stats */}
          {isLoading ? (
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-1">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            stats && (
              <>
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-1">
                  <KbStat label="Articles" value={String(stats.total)} />
                  <KbStat
                    label="Stale"
                    value={String(stats.stale)}
                    tone={stats.stale > 0 ? 'warn' : undefined}
                  />
                  <KbStat label="Categories" value={String(Object.keys(stats.byCategory).length)} />
                  <KbStat label="Runs today" value={hestia ? String(hestia.runs_today) : '--'} />
                </div>

                {/* Category breakdown */}
                {Object.keys(stats.byCategory).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {Object.entries(stats.byCategory).map(([cat, count]) => (
                      <span
                        key={cat}
                        className="bg-cmd-high px-2 py-0.5 font-mono text-[0.6rem] text-cmd-outline"
                      >
                        {KB_CATEGORY_LABELS[cat] ?? cat}
                        <span className="text-white ml-1 tabular-nums">{count}</span>
                      </span>
                    ))}
                  </div>
                )}

                {/* Recent activity */}
                <div className="mt-4">
                  <p className="font-label text-[0.55rem] tracking-widest text-cmd-outline uppercase">
                    Recent activity
                  </p>
                  {stats.recent.length === 0 ? (
                    <p className="mt-1 font-mono text-[0.65rem] text-cmd-outline-variant">
                      No articles yet — run the seed or add one via the API.
                    </p>
                  ) : (
                    <ul className="mt-1.5 space-y-1">
                      {stats.recent.map((r) => (
                        <li key={r.id} className="flex items-center justify-between gap-3">
                          <span className="font-label text-xs text-white truncate">{r.title}</span>
                          <span className="font-mono text-[0.6rem] text-cmd-outline shrink-0 uppercase">
                            {KB_CATEGORY_LABELS[r.category] ?? r.category} · {timeAgo(r.updatedAt)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )
          )}
        </div>
      </div>
    </div>
  )
}
